import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, User, LogOut } from "lucide-react";
import { Logo } from "./Logo";
import { scrollToSection } from "@/lib/scroll";
import { CONTACT } from "@/data/content";
import { useAuth } from "@/context/AuthContext";

const LINKS = [
  { label: "Destinations", href: "#destinations" },
  { label: "Gallery", href: "#gallery" },
  { label: "Services", href: "#services" },
  { label: "About", href: "#about" },
  { label: "Reviews", href: "#reviews" },
  { label: "Contact", href: "#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    if (!document.querySelector(href)) {
      window.location.href = "/" + href;
      return;
    }
    setTimeout(() => scrollToSection(href), open ? 350 : 0);
  };

  const handleLogout = async () => {
    await logout();
    window.location.href = "/";
  };

  return (
    <>
      <header className="fixed top-0 inset-x-0 z-50">
        <div
          className={`overflow-hidden transition-all duration-500 ${
            scrolled ? "max-h-0" : "max-h-10"
          } bg-[#d4af37] text-[#0A1128]`}
        >
          <div className="max-w-7xl mx-auto px-6 h-10 flex items-center justify-center gap-2 text-xs tracking-[0.15em] uppercase font-medium">
            <span>Visit Visa & Entry Permission Specialists — UAE</span>
            <a
              href={`tel:${CONTACT.phoneRaw}`}
              data-testid="announcement-call-link"
              className="hidden sm:flex items-center gap-1 underline underline-offset-2"
            >
              <Phone size={11} /> {CONTACT.phoneDisplay}
            </a>
            <a
              href={`tel:${CONTACT.phone2Raw}`}
              data-testid="announcement-call-link-2"
              className="hidden md:flex items-center gap-1 underline underline-offset-2"
            >
              <Phone size={11} /> {CONTACT.phone2Display}
            </a>
          </div>
        </div>
        <nav
          data-testid="main-nav"
          className={`transition-all duration-500 ${
            scrolled
              ? "bg-[#101C3E]/85 backdrop-blur-xl border-b border-[#d4af37]/20 shadow-[0_8px_32px_rgba(0,0,0,0.4)]"
              : "bg-transparent border-b border-transparent"
          }`}
        >
          <div className="max-w-7xl mx-auto px-6 h-[72px] flex items-center justify-between">
            <Logo onClick={() => go("#top")} />
            <div className="hidden lg:flex items-center gap-8">
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  data-testid={`nav-link-${l.label.toLowerCase()}`}
                  onClick={(e) => {
                    e.preventDefault();
                    go(l.href);
                  }}
                  className="text-sm tracking-[0.12em] uppercase text-slate-300 hover:text-[#d4af37] transition-colors duration-300"
                >
                  {l.label}
                </a>
              ))}
            </div>
            <div className="flex items-center gap-5">
              {user === false && (
                <Link
                  to="/auth"
                  data-testid="nav-signin-link"
                  className="hidden sm:flex items-center gap-2 text-sm tracking-[0.12em] uppercase text-slate-300 hover:text-[#d4af37] transition-colors"
                >
                  <User size={15} /> Sign In
                </Link>
              )}
              {user && (
                <>
                  <Link
                    to={user.role === "admin" ? "/admin" : "/account"}
                    data-testid="nav-account-link"
                    className="hidden sm:flex items-center gap-2 text-sm tracking-[0.12em] uppercase text-[#d4af37] hover:text-[#f3e5ab] transition-colors"
                  >
                    <User size={15} /> {user.role === "admin" ? "Dashboard" : "My Trips"}
                  </Link>
                  <button
                    onClick={handleLogout}
                    data-testid="nav-logout-button"
                    aria-label="Sign out"
                    className="hidden sm:block text-slate-400 hover:text-[#d4af37] transition-colors"
                  >
                    <LogOut size={16} />
                  </button>
                </>
              )}
              <button
                data-testid="nav-cta-button"
                onClick={() => go("#booking")}
                className="hidden sm:block border border-[#d4af37] text-[#d4af37] px-6 py-2.5 text-sm tracking-[0.12em] uppercase transition-colors duration-300 hover:bg-[#d4af37] hover:text-[#0A1128]"
              >
                Book Now
              </button>
              <button
                data-testid="mobile-menu-button"
                onClick={() => setOpen(true)}
                className="lg:hidden text-slate-50 p-2"
                aria-label="Open menu"
              >
                <Menu size={26} />
              </button>
            </div>
          </div>
        </nav>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[60] bg-[#0A1128]/98 backdrop-blur-xl flex flex-col"
          >
            <div className="flex items-center justify-between px-6 h-[72px]">
              <Logo onClick={() => go("#top")} />
              <button
                data-testid="mobile-menu-close"
                onClick={() => setOpen(false)}
                className="text-slate-50 p-2"
                aria-label="Close menu"
              >
                <X size={28} />
              </button>
            </div>
            <div className="flex-1 flex flex-col justify-center px-8 gap-2 overflow-y-auto">
              {LINKS.map((l, i) => (
                <motion.a
                  key={l.href}
                  href={l.href}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 * i, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                  onClick={(e) => {
                    e.preventDefault();
                    go(l.href);
                  }}
                  className="font-display text-4xl sm:text-5xl font-light text-slate-50 hover:text-[#d4af37] transition-colors py-2 border-b border-[#d4af37]/10"
                >
                  {l.label}
                </motion.a>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.5 }}
                className="flex flex-col gap-4 mt-8"
              >
                <button
                  data-testid="mobile-nav-cta"
                  onClick={() => go("#booking")}
                  className="border border-[#d4af37] text-[#d4af37] px-8 py-4 text-sm tracking-[0.15em] uppercase hover:bg-[#d4af37] hover:text-[#0A1128] transition-colors"
                >
                  Book Now
                </button>
                {user === false && (
                  <Link
                    to="/auth"
                    data-testid="mobile-nav-signin"
                    onClick={() => setOpen(false)}
                    className="flex items-center justify-center gap-2 border border-slate-50/30 text-slate-200 px-8 py-4 text-sm tracking-[0.15em] uppercase hover:border-[#d4af37] hover:text-[#d4af37] transition-colors"
                  >
                    <User size={16} /> Sign In
                  </Link>
                )}
                {user && (
                  <>
                    <Link
                      to={user.role === "admin" ? "/admin" : "/account"}
                      data-testid="mobile-nav-account"
                      onClick={() => setOpen(false)}
                      className="flex items-center justify-center gap-2 border border-slate-50/30 text-slate-200 px-8 py-4 text-sm tracking-[0.15em] uppercase hover:border-[#d4af37] hover:text-[#d4af37] transition-colors"
                    >
                      <User size={16} /> {user.role === "admin" ? "Dashboard" : "My Trips"}
                    </Link>
                    <button
                      data-testid="mobile-nav-logout"
                      onClick={handleLogout}
                      className="flex items-center justify-center gap-2 text-slate-400 px-8 py-3 text-sm tracking-[0.15em] uppercase hover:text-[#d4af37] transition-colors"
                    >
                      <LogOut size={16} /> Sign Out
                    </button>
                  </>
                )}
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
